import type { Brand, IntegrationContractResult, IntegrationResource } from '@common/api/brandGroupService';

type IntegrationConnectionIdentity = {
  id?: string | number | null;
  integrationId?: string | number | null;
  integration_id?: string | number | null;
  connectionId?: string | number | null;
  connection_id?: string | number | null;
  connectionBackend?: string | null;
  connection_backend?: string | null;
  externalConnectionId?: string | number | null;
  external_connection_id?: string | number | null;
  mappings?: readonly {
    id?: string | number | null;
    connectionId?: string | number | null;
    connection_id?: string | number | null;
    nangoConnectionId?: string | number | null;
    nango_connection_id?: string | number | null;
    connectionBackend?: string | null;
    connection_backend?: string | null;
    externalConnectionId?: string | number | null;
    external_connection_id?: string | number | null;
  }[];
};

const SECONDARY_RESOURCE_TYPES: Record<string, 'CUSTOMER' | 'PROPERTY' | 'SITE'> = {
  'google-ads': 'CUSTOMER',
  'google-analytics': 'PROPERTY',
  'google-search-console': 'SITE',
};

export const normalizeOnboardingResourceKey = (
  resourceType: string,
  resourceKey: string | number | null | undefined,
): string => {
  const key = String(resourceKey ?? '').trim();
  switch (String(resourceType || '').trim().toUpperCase()) {
    case 'CUSTOMER':
      return key.replace(/^customers\//i, '').replace(/-/g, '');
    case 'PROPERTY':
      return `properties/${key.replace(/^properties\//i, '')}`;
    default:
      return key;
  }
};

export const getOnboardingSecondaryResourceType = (provider: string) =>
  SECONDARY_RESOURCE_TYPES[String(provider || '').trim().toLowerCase()];

/**
 * The contract normally exposes the database integration id, while older
 * responses (and a few connector-specific paths) can still carry the opaque
 * Nango connection id.  Do not coerce either value to Number: Number('abc')
 * is NaN and NaN !== NaN, which silently drops every secondary resource.
 */
const sameOnboardingConnection = (
  left: string | number | null | undefined,
  right: string | number | null | undefined,
) => {
  const leftValue = String(left ?? '').trim();
  const rightValue = String(right ?? '').trim();
  if (!leftValue || !rightValue) return false;
  if (leftValue === rightValue) return true;

  const leftNumber = Number(leftValue);
  const rightNumber = Number(rightValue);
  return Number.isSafeInteger(leftNumber)
    && Number.isSafeInteger(rightNumber)
    && leftNumber === rightNumber;
};

export const selectOnboardingSecondaryResources = (
  resources: IntegrationResource[],
  provider: string,
  connectionId: string | number | Array<string | number | null | undefined>,
): IntegrationResource[] => {
  const resourceType = getOnboardingSecondaryResourceType(provider);
  if (!resourceType) return [];
  const connectionIds = (Array.isArray(connectionId) ? connectionId : [connectionId])
    .filter((value): value is string | number => value !== undefined && value !== null && String(value).trim() !== '');
  return resources.filter((resource) =>
    String(resource.status || '').toUpperCase() === 'ACTIVE'
      && connectionIds.some((candidate) => sameOnboardingConnection(resource.connectionId, candidate))
      && String(resource.resourceType || '').toUpperCase() === resourceType,
  );
};

export const resolveDefaultBusinessGroupId = (brands: Brand[]): string | null =>
  brands.find((brand) => brand.isDefault || brand.default)?.id || null;

type RefreshGoogleResourcesResult = {
  resources?: IntegrationResource[] | null;
};

const getRefreshedGoogleResources = (value: unknown): IntegrationResource[] => {
  if (!value || typeof value !== 'object') return [];
  const resources = (value as RefreshGoogleResourcesResult).resources;
  return Array.isArray(resources) ? resources : [];
};

const buildInlineGoogleResourceContract = (
  resources: IntegrationResource[],
): IntegrationContractResult => ({
  integrations: [],
  unsupportedProviders: [],
  complete: true,
  resources,
});

/**
 * Google OAuth completion performs one explicit resource refresh. Prefer the
 * returned child resources, but fall back to one canonical contract read when
 * the refresh fails or returns no usable rows. The connection registration can
 * already have persisted those resources, so treating an empty refresh as the
 * final answer would show a false empty picker.
 */
export const loadOnboardingResourceSelectionData = async ({
  loadContract,
  loadBrands,
  refreshResources,
  provider,
  integrationId,
  connectionId,
  requireDefaultBusinessGroup = true,
}: {
  loadContract: () => Promise<IntegrationContractResult>;
  loadBrands: () => Promise<Brand[]>;
  refreshResources?: () => Promise<unknown>;
  provider: string;
  integrationId: number;
  connectionId?: string | number | null;
  requireDefaultBusinessGroup?: boolean;
}): Promise<{ contract: IntegrationContractResult; brands: Brand[]; resources: IntegrationResource[] }> => {
  let refreshedResources: IntegrationResource[] = [];
  if (refreshResources) {
    try {
      const refreshed = await refreshResources();
      refreshedResources = selectOnboardingSecondaryResources(
        getRefreshedGoogleResources(refreshed),
        provider,
        [integrationId, connectionId],
      );
    } catch {
      // The canonical contract fallback below can still expose resources that
      // were persisted by connection registration before this refresh failed.
    }
  }
  let lastBrands: Brand[] = [];
  try {
    lastBrands = await loadBrands();
  } catch (error) {
    if (requireDefaultBusinessGroup) {
      throw error;
    }
  }
  if (refreshedResources.length > 0) {
    return {
      contract: buildInlineGoogleResourceContract(refreshedResources),
      brands: lastBrands,
      resources: refreshedResources,
    };
  }
  const lastContract = await loadContract();
  return {
    contract: lastContract,
    brands: lastBrands,
    resources: selectOnboardingSecondaryResources(lastContract.resources, provider, [integrationId, connectionId]),
  };
};

/**
 * OAuth callbacks do not always return the connection id in the immediate
 * response. The refreshed integration snapshot carries it on its mapping;
 * use the newest mapping as the connection created by this onboarding flow.
 */
export const resolveOnboardingConnectionId = (
  integration?: IntegrationConnectionIdentity | null,
): string | null => {
  const mappings = integration?.mappings || [];
  const mapping = [...mappings].reverse().find((candidate) =>
    candidate.nangoConnectionId
    || candidate.nango_connection_id
    || candidate.connectionId
    || candidate.connection_id
    || candidate.externalConnectionId
    || candidate.external_connection_id,
  );
  // The refresh endpoint matches the persisted backend connection identity.
  // Bustly-managed connections intentionally store this as `bustly:<uuid>`,
  // while `connectionId` is the provider-facing bare UUID. Prefer the stored
  // identity so both managed and legacy Nango connections resolve correctly.
  const value = mapping?.nangoConnectionId
    || mapping?.nango_connection_id
    || mapping?.connectionId
    || mapping?.connection_id
    || mapping?.externalConnectionId
    || mapping?.external_connection_id
    || integration?.connectionId
    || integration?.connection_id
    || integration?.externalConnectionId
    || integration?.external_connection_id;
  const normalizedValue = value === undefined || value === null ? '' : String(value).trim();
  if (!normalizedValue) return null;

  const backend = String(
    mapping?.connectionBackend
    || mapping?.connection_backend
    || integration?.connectionBackend
    || integration?.connection_backend
    || '',
  ).trim().toUpperCase();
  if (backend === 'BUSTLY' && !normalizedValue.toLowerCase().startsWith('bustly:')) {
    return `bustly:${normalizedValue}`;
  }
  return normalizedValue;
};

/**
 * Resource assignment endpoints require the numeric workspace integration id.
 * OAuth/Nango connection ids are opaque strings and must never be coerced to
 * numbers (NaN would create a failed parent assignment).
 */
export const resolveOnboardingWorkspaceIntegrationId = (
  integration?: IntegrationConnectionIdentity | null,
  expectedConnectionId?: string | number | null,
): number | null => {
  const normalizedExpectedConnectionId = String(expectedConnectionId ?? '').trim().toLowerCase();
  const mappings = integration?.mappings || [];
  const matchingMapping = normalizedExpectedConnectionId
    ? [...mappings].reverse().find((mapping) => [
      mapping.connectionId,
      mapping.connection_id,
      mapping.nangoConnectionId,
      mapping.nango_connection_id,
    ].some((candidate) => String(candidate ?? '').trim().toLowerCase() === normalizedExpectedConnectionId))
    : undefined;
  const candidates = [
    ...(matchingMapping ? [matchingMapping.id] : []),
    ...mappings
      .slice()
      .reverse()
      .map((mapping) => mapping.id),
    integration?.integrationId,
    integration?.integration_id,
    integration?.id,
  ];
  for (const candidate of candidates) {
    const text = String(candidate ?? '').trim();
    if (!/^\d+$/.test(text)) continue;
    const value = Number(text);
    if (Number.isSafeInteger(value) && value > 0) return value;
  }
  return null;
};
